import React, {
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState,
} from "react";
import BranchGroupsProvider from "./BranchGroupsProvider";
import { SearchGroupList } from "../../../model/branch/BranchGroupsModel";
import UtilityHelper from "../../../service/utilities/UtilityHelper";
import { useDispatch } from "react-redux";
import { updateGroup } from "../../../libraries/state_reducers/widget/DetailConnectGroupReducer";
import { addModal } from "../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../const/ModalKeys";

type BranchGroupsContextType = {
  branchGroupList: Array<SearchGroupList> | null;
  branchGroupsInit: () => void;
  onClickGroup: (detail: SearchGroupList) => void;

  showSearch: boolean;
  searchValue: string;
  handleOnChange: (name: string, value: string) => void;
  clearSearch: () => void;
  updateShowSearch: () => void;
};

const BranchGroupsContext = createContext<BranchGroupsContextType | undefined>(
  undefined
);

export const BranchGroupsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();

  const searchValueRef = useRef<string>("");
  const groupListResultRef = useRef<Array<SearchGroupList> | []>([]);

  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const [branchGroupList, setBranchGroupList] =
    useState<Array<SearchGroupList> | null>(null);

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    if (fieldName === `search-branch-group`) {
      setSearchValue(fieldValue);
      searchValueRef.current = fieldValue;

      const filtered: Array<SearchGroupList> | undefined =
        groupListResultRef.current?.filter((group) =>
          Object.values(group)
            .join(" ")
            .toLowerCase()
            .includes(searchValueRef.current.toLowerCase())
        );

      setBranchGroupList(filtered!);
    }
  };

  const clearSearch = () => {
    if (UtilityHelper.isEmpty(searchValueRef.current)) {
      updateShowSearch();
    }
    setBranchGroupList(groupListResultRef.current);
    setSearchValue("");
    searchValueRef.current = "";
  };

  const updateShowSearch = () => {
    setShowSearch(!showSearch);
  };

  const branchGroupsInit = async () => {
    const branchGroupListModel = await BranchGroupsProvider.getGroupListAPI();
    if (branchGroupListModel !== null) {
      const groupListResult: Array<SearchGroupList> | null =
        branchGroupListModel?.result?.search_group_list!;

      setBranchGroupList(groupListResult);
      groupListResultRef.current = groupListResult;
    }
  };

  const onClickGroup = (detail: SearchGroupList) => {
    dispatch(updateGroup(detail));
    dispatch(
      addModal({
        id: ModalKeys.connect_detail_branch_group,
        title: detail?.group_title!,
        size: "md",
        hideHeader: true,
      })
    );
  };

  return (
    <BranchGroupsContext.Provider
      value={{
        branchGroupList,
        branchGroupsInit,
        onClickGroup,

        showSearch,
        searchValue,
        handleOnChange,
        clearSearch,
        updateShowSearch,
      }}
    >
      {children}
    </BranchGroupsContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const BranchGroupsContextImport = () => {
  const context = useContext(BranchGroupsContext);
  if (!context) {
    throw new Error(
      "BranchGroupsContextImport must be used within a BranchGroupsContextProvider"
    );
  }
  return context;
};
